import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import JSZip from 'jszip';
import { CustomToastService } from 'src/app/core/services/custom-toast.service';
import { MasterService } from 'src/app/core/services/master.service';
import { trackById } from 'src/app/core/utils/track-by';
import {
  BulkUploadEntityType,
  BulkUploadIconsResponse,
  BulkUploadStatus,
} from 'src/app/shared/models/shared.models';
import { ProductDepartmentsService } from 'src/app/shared/modules/product-departments/services/product-departments.service';

@Component({
  selector: 'app-bulk-upload-icons',
  templateUrl: './bulk-upload-icons.component.html',
  styleUrls: ['./bulk-upload-icons.component.css'],
  standalone: false,
})
export class BulkUploadIconsComponent implements OnInit {
  trackById = trackById;
  @Input() entityType!: BulkUploadEntityType;
  @ViewChild('fileInput') fileInput!: ElementRef;

  loading: boolean = false;
  uploadResult: BulkUploadIconsResponse | null = null;
  selectedFile: File | null = null;
  fileSizeError: string | null = null;
  BulkUploadStatus = BulkUploadStatus;
  companyId: number = 0;

  constructor(
    private departmentService: ProductDepartmentsService,
    private toastService: CustomToastService,
    private masterService: MasterService,
  ) {}

  ngOnInit() {
    const userInfo = this.masterService.getUserDetailFromStorage();
    this.companyId = userInfo?.companyid || 0;
  }

  get entityTypeName(): string {
    return this.entityType === BulkUploadEntityType.Department ? 'Department' : 'Category';
  }

  triggerFileInput() {
    this.fileInput.nativeElement.click();
  }

  async onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.fileSizeError = null;
      if (file.name.endsWith('.zip')) {
        const maxSizeInMB = 100;
        const fileSizeInMB = file.size / (1024 * 1024);
        if (fileSizeInMB > maxSizeInMB) {
          this.fileSizeError = `Max file size allowed is ${maxSizeInMB}MB and current file size is ${fileSizeInMB.toFixed(2)}MB`;
        }
        try {
          const zip = new JSZip();
          const content = await zip.loadAsync(file);

          const csvFile = Object.keys(content.files).find((name) =>
            name.toLowerCase().endsWith('.csv'),
          );

          if (csvFile) {
            const csvContent = await content.files[csvFile].async('string');
            const headers = csvContent
              .split('\n')[0]
              .split(',')
              .map((h: string) => h.trim());

            if (this.entityType === BulkUploadEntityType.Category) {
              const hasName = headers.some((h: string) => h.toLowerCase() === 'name');
              const hasProductDepartment = headers.some(
                (h: string) => h.toLowerCase() === 'product_department',
              );

              if (!hasName || !hasProductDepartment) {
                this.toastService.error(
                  `Invalid ${csvFile} file. Name and Product_Department columns are required for Category upload.`,
                );
                this.resetFileInput();
                return;
              }
            } else if (this.entityType === BulkUploadEntityType.Department) {
              const hasName = headers.some((h: string) => h.toLowerCase() === 'name');
              const hasProductDepartment = headers.some(
                (h: string) => h.toLowerCase() === 'product_department',
              );

              if (!hasName) {
                this.toastService.error(`Invalid ${csvFile} file. Name column is required.`);
                this.resetFileInput();
                return;
              }

              if (hasProductDepartment) {
                this.toastService.error(
                  `Invalid ${csvFile} file. Product_Department column should not be present for Department upload.`,
                );
                this.resetFileInput();
                return;
              }
            }
          } else {
            this.toastService.error('No CSV file found in zip.');
            this.resetFileInput();
            return;
          }

          this.selectedFile = file;
          this.uploadResult = null;
        } catch (e) {
          console.error('Error reading zip file', e);
          this.toastService.error('Failed to read ZIP file.');
          this.resetFileInput();
        }
      } else {
        this.toastService.error('Please select a ZIP file.');
        this.resetFileInput();
      }
    }
  }

  resetFileInput() {
    this.selectedFile = null;
    this.fileSizeError = null;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  uploadIcons() {
    if (!this.selectedFile) return;

    this.loading = true;
    this.departmentService
      .bulkUploadIcons(this.selectedFile, this.companyId, this.entityType)
      .subscribe({
        next: (res) => {
          this.uploadResult = res;
          this.loading = false;
          this.toastService.success('Bulk upload completed.');
        },
        error: (err) => {
          this.loading = false;
          this.toastService.error('Failed to upload icons.');
          console.error(err);
        },
      });
  }

  downloadTemplate() {
    let csvContent = '';
    if (this.entityType === BulkUploadEntityType.Department) {
      csvContent =
        'Name,Description,External_Id,Sort_Order,IconFileName\nElectronics,Electronics Department,EXT001,1,electronics-icon.png\nClothing,Clothing Department,EXT002,2,clothing-icon.png';
    } else {
      csvContent =
        'Name,Description,External_Id,Product_Department,Category_Type,Parent_Category,printLabelType,IconFileName\nSmartphones,Smartphones Category,EXT_CAT_001,Electronics,Standard,,Default,iphone.png\nT-Shirts,T-Shirts Category,EXT_CAT_002,Clothing,Standard,,Default,tshirt.png';
    }
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.entityTypeName}_Icon_Template.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  closeModal() {
    this.resetFileInput();
    this.uploadResult = null;
  }
}
